import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import { ArrowLeft, Check, X, Zap, Rocket, Building2, Crown, ExternalLink, Code, ChevronDown } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const CURRENCIES = {
  USD: { symbol: '$', rate: 1, locale: 'en-US', decimals: 0, flag: 'US' },
  EUR: { symbol: '\u20AC', rate: 0.92, locale: 'de-DE', decimals: 0, flag: 'EU' },
  GBP: { symbol: '\u00A3', rate: 0.79, locale: 'en-GB', decimals: 0, flag: 'GB' },
  INR: { symbol: '\u20B9', rate: 83.5, locale: 'en-IN', decimals: 0, flag: 'IN' },
  AUD: { symbol: 'A$', rate: 1.55, locale: 'en-AU', decimals: 0, flag: 'AU' },
  CAD: { symbol: 'C$', rate: 1.36, locale: 'en-CA', decimals: 0, flag: 'CA' },
  SGD: { symbol: 'S$', rate: 1.34, locale: 'en-SG', decimals: 0, flag: 'SG' },
  JPY: { symbol: '\u00A5', rate: 150.2, locale: 'ja-JP', decimals: 0, flag: 'JP' },
  CHF: { symbol: 'CHF', rate: 0.88, locale: 'de-CH', decimals: 0, flag: 'CH' },
  CNY: { symbol: '\u00A5', rate: 7.24, locale: 'zh-CN', decimals: 0, flag: 'CN' },
};

const FLAG_EMOJIS = {
  US: '\uD83C\uDDFA\uD83C\uDDF8', EU: '\uD83C\uDDEA\uD83C\uDDFA', GB: '\uD83C\uDDEC\uD83C\uDDE7',
  IN: '\uD83C\uDDEE\uD83C\uDDF3', AU: '\uD83C\uDDE6\uD83C\uDDFA', CA: '\uD83C\uDDE8\uD83C\uDDE6',
  SG: '\uD83C\uDDF8\uD83C\uDDEC', JP: '\uD83C\uDDEF\uD83C\uDDF5', CH: '\uD83C\uDDE8\uD83C\uDDED',
  CN: '\uD83C\uDDE8\uD83C\uDDF3',
};

const TIER_META = {
  developer: { icon: Code, color: 'hsl(174, 68%, 35%)', gradient: 'linear-gradient(135deg, hsl(174, 68%, 95%), hsl(174, 68%, 85%))' },
  builder: { icon: Zap, color: 'hsl(168, 70%, 45%)', gradient: 'linear-gradient(135deg, hsl(168, 70%, 95%), hsl(168, 70%, 88%))' },
  pro: { icon: Rocket, color: 'hsl(210, 70%, 50%)', gradient: 'linear-gradient(135deg, hsl(210, 70%, 95%), hsl(210, 70%, 85%))', highlight: true },
  team: { icon: Building2, color: 'hsl(270, 60%, 55%)', gradient: 'linear-gradient(135deg, hsl(270, 60%, 95%), hsl(270, 60%, 88%))' },
  enterprise: { icon: Crown, color: 'hsl(40, 80%, 50%)', gradient: 'linear-gradient(135deg, hsl(40, 80%, 95%), hsl(40, 80%, 88%))' },
};

function formatPrice(usdPrice, currency) {
  const cur = CURRENCIES[currency];
  const converted = Math.round(usdPrice * cur.rate);
  return `${cur.symbol}${converted.toLocaleString(cur.locale)}`;
}

function CurrencySelector({ currency, onChange }) {
  const [open, setOpen] = useState(false);
  const cur = CURRENCIES[currency];

  useEffect(() => {
    if (!open) return;
    const close = (e) => {
      if (!e.target.closest('[data-testid="currency-selector"]')) setOpen(false);
    };
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, [open]);

  return (
    <div style={{ position: 'relative', display: 'inline-block' }} data-testid="currency-selector">
      <button
        onClick={() => setOpen(!open)}
        data-testid="currency-selector-trigger"
        style={{
          display: 'flex', alignItems: 'center', gap: '0.5rem',
          padding: '0.5rem 0.875rem', borderRadius: '9999px',
          border: '1px solid hsl(var(--border))', background: 'hsl(var(--card))',
          color: 'hsl(var(--foreground))', cursor: 'pointer', fontSize: '0.875rem',
          fontWeight: 600, fontFamily: 'IBM Plex Sans, sans-serif',
          transition: 'border-color 0.15s, box-shadow 0.15s',
          boxShadow: open ? 'var(--shadow-ring)' : 'none',
          borderColor: open ? 'hsl(var(--ring))' : undefined,
        }}
      >
        <span>{FLAG_EMOJIS[cur.flag]}</span>
        <span>{currency}</span>
        <ChevronDown size={14} style={{ opacity: 0.5, transition: 'transform 0.2s', transform: open ? 'rotate(180deg)' : 'none' }} />
      </button>
      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 6px)', left: '50%', transform: 'translateX(-50%)',
          background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))',
          borderRadius: 'var(--radius)', boxShadow: 'var(--shadow-md)',
          padding: '0.375rem', zIndex: 50, minWidth: '160px',
          animation: 'fadeIn 0.15s ease',
        }} data-testid="currency-dropdown">
          {Object.keys(CURRENCIES).map((code) => (
            <button
              key={code}
              data-testid={`currency-option-${code}`}
              onClick={() => { onChange(code); setOpen(false); }}
              style={{
                display: 'flex', alignItems: 'center', gap: '0.625rem',
                width: '100%', padding: '0.5rem 0.75rem', border: 'none',
                background: code === currency ? 'hsl(var(--accent) / 0.1)' : 'transparent',
                color: code === currency ? 'hsl(var(--accent))' : 'hsl(var(--foreground))',
                borderRadius: '0.375rem', cursor: 'pointer', fontSize: '0.8125rem',
                fontWeight: code === currency ? 600 : 400, fontFamily: 'IBM Plex Sans, sans-serif',
                transition: 'background-color 0.1s',
              }}
              onMouseEnter={(e) => { if (code !== currency) e.currentTarget.style.background = 'hsl(var(--secondary))'; }}
              onMouseLeave={(e) => { if (code !== currency) e.currentTarget.style.background = 'transparent'; }}
            >
              <span style={{ fontSize: '1rem' }}>{FLAG_EMOJIS[CURRENCIES[code].flag]}</span>
              <span>{code}</span>
              <span style={{ marginLeft: 'auto', opacity: 0.5, fontSize: '0.75rem' }}>{CURRENCIES[code].symbol}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function PricingPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [plans, setPlans] = useState({});
  const [currentPlan, setCurrentPlan] = useState('free');
  const [loading, setLoading] = useState(true);
  const [subscribing, setSubscribing] = useState(null);
  const [currency, setCurrency] = useState(() => {
    try { return localStorage.getItem('cf-currency') || 'USD'; } catch { return 'USD'; }
  });

  useEffect(() => {
    try { localStorage.setItem('cf-currency', currency); } catch {}
  }, [currency]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [plansRes, subRes] = await Promise.all([
          fetch(`${API_URL}/api/plans`),
          fetch(`${API_URL}/api/subscription`, { credentials: 'include' }),
        ]);
        if (plansRes.ok) {
          const plansData = await plansRes.json();
          setPlans(plansData.plans || {});
        }
        if (subRes.ok) {
          const subData = await subRes.json();
          setCurrentPlan(subData.plan || 'free');
        }
      } catch (err) {
        console.error('Failed to fetch plans:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleSubscribe = async (planId) => {
    if (planId === 'enterprise') {
      window.location.href = 'mailto:enterprise@codefoundry.sh?subject=Enterprise%20Plan%20Inquiry';
      return;
    }
    setSubscribing(planId);
    try {
      const res = await fetch(`${API_URL}/api/subscription/checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ plan_id: planId, origin_url: window.location.origin }),
        credentials: 'include',
      });
      if (res.ok) {
        const data = await res.json();
        if (data.url) window.location.href = data.url;
      } else {
        const err = await res.json();
        alert(err.detail || 'Failed to start checkout');
      }
    } catch (err) {
      console.error('Checkout error:', err);
    } finally {
      setSubscribing(null);
    }
  };

  const tierOrder = ['developer', 'builder', 'pro', 'team', 'enterprise'];

  return (
    <div data-testid="pricing-page">
      <button onClick={() => navigate('/')} className="btn btn-ghost" style={{ marginBottom: '1.5rem', fontSize: '0.8125rem' }} data-testid="back-button">
        <ArrowLeft size={16} /> Back
      </button>

      <div style={{ textAlign: 'center', maxWidth: '700px', margin: '0 auto 2.5rem' }}>
        <h1 style={{ fontSize: '2rem', fontWeight: 700, fontFamily: 'Space Grotesk, sans-serif', marginBottom: '0.5rem' }}>
          Choose your plan
        </h1>
        <p style={{ color: 'hsl(var(--muted-foreground))', fontSize: '1rem', marginBottom: '1.25rem' }}>
          Scale your AI-powered software factory. All plans include guided builds with deterministic agents and milestone approvals.
        </p>
        <CurrencySelector currency={currency} onChange={setCurrency} />
        {currency !== 'USD' && (
          <p style={{ fontSize: '0.6875rem', color: 'hsl(var(--muted-foreground))', marginTop: '0.5rem', opacity: 0.7 }} data-testid="currency-note">
            Prices shown in {currency} are approximate. Checkout is processed in USD.
          </p>
        )}
      </div>

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '3rem' }}><div className="spinner" /></div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1.25rem', maxWidth: '1100px', margin: '0 auto' }}>
          {tierOrder.map((planId) => {
            const plan = plans[planId];
            if (!plan) return null;
            const meta = TIER_META[planId] || {};
            const Icon = meta.icon || Zap;
            const isHighlight = meta.highlight;
            const isCurrent = currentPlan === planId;
            const isEnterprise = planId === 'enterprise';

            return (
              <div
                key={planId}
                className="card"
                style={{
                  padding: '1.75rem',
                  border: isHighlight ? `2px solid ${meta.color}` : isCurrent ? '2px solid hsl(var(--accent))' : '1px solid hsl(var(--border))',
                  position: 'relative',
                  display: 'flex',
                  flexDirection: 'column',
                }}
                data-testid={`plan-${planId}`}
              >
                {isHighlight && (
                  <div style={{
                    position: 'absolute', top: '-12px', left: '50%', transform: 'translateX(-50%)',
                    background: meta.color, color: 'white',
                    padding: '0.125rem 0.75rem', borderRadius: '9999px',
                    fontSize: '0.6875rem', fontWeight: 600, letterSpacing: '0.03em',
                  }}>MOST POPULAR</div>
                )}
                {isCurrent && (
                  <div style={{
                    position: 'absolute', top: '-12px', left: '50%', transform: 'translateX(-50%)',
                    background: 'hsl(var(--accent))', color: 'white',
                    padding: '0.125rem 0.75rem', borderRadius: '9999px',
                    fontSize: '0.6875rem', fontWeight: 600,
                  }}>CURRENT PLAN</div>
                )}

                {/* Icon + Name */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.625rem', marginBottom: '1rem' }}>
                  <div style={{
                    width: '40px', height: '40px', borderRadius: '0.625rem',
                    background: meta.gradient, display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <Icon size={20} style={{ color: meta.color }} />
                  </div>
                  <h3 style={{ fontSize: '1.125rem', fontWeight: 700, fontFamily: 'Space Grotesk, sans-serif' }}>{plan.label}</h3>
                </div>

                {/* Price */}
                <div style={{ marginBottom: '1.25rem' }}>
                  {isEnterprise ? (
                    <span style={{ fontSize: '1.5rem', fontWeight: 700, fontFamily: 'Space Grotesk, sans-serif' }}>Custom</span>
                  ) : (
                    <>
                      <span style={{ fontSize: '2.25rem', fontWeight: 700, fontFamily: 'Space Grotesk, sans-serif' }} data-testid={`price-${planId}`}>
                        {formatPrice(plan.price_monthly, currency)}
                      </span>
                      <span style={{ fontSize: '0.875rem', color: 'hsl(var(--muted-foreground))' }}>/month</span>
                    </>
                  )}
                </div>

                {/* Key metrics */}
                {!isEnterprise && (
                <div style={{
                  background: 'hsl(var(--secondary) / 0.5)', borderRadius: 'var(--radius)',
                  padding: '0.75rem', marginBottom: '1rem', fontSize: '0.8125rem',
                }}>
                  {plan.completions_included && !plan.builds_included ? (
                    <>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.375rem' }}>
                        <span style={{ color: 'hsl(var(--muted-foreground))' }}>Completions/month</span>
                        <strong>{plan.completions_included?.toLocaleString()}</strong>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ color: 'hsl(var(--muted-foreground))' }}>Model</span>
                        <strong>Haiku (fast)</strong>
                      </div>
                    </>
                  ) : (
                    <>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.375rem' }}>
                        <span style={{ color: 'hsl(var(--muted-foreground))' }}>Builds/month</span>
                        <strong>{plan.builds_included}</strong>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.375rem' }}>
                        <span style={{ color: 'hsl(var(--muted-foreground))' }}>Parallel builds</span>
                        <strong>{plan.parallel_builds}</strong>
                      </div>
                      {plan.overage_cost !== null && (
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                          <span style={{ color: 'hsl(var(--muted-foreground))' }}>Overage</span>
                          <strong>{formatPrice(plan.overage_cost, currency)}/build</strong>
                        </div>
                      )}
                    </>
                  )}
                </div>
                )}

                {/* Features */}
                <div style={{ flex: 1, marginBottom: '1.25rem' }}>
                  {plan.features.map((f, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.8125rem', marginBottom: '0.375rem' }}>
                      <Check size={14} style={{ color: meta.color, flexShrink: 0 }} />
                      <span>{f}</span>
                    </div>
                  ))}
                  {(plan.no_features || []).map((f, i) => (
                    <div key={`no-${i}`} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.8125rem', marginBottom: '0.375rem', opacity: 0.4 }}>
                      <X size={14} style={{ flexShrink: 0 }} />
                      <span>{f}</span>
                    </div>
                  ))}
                </div>

                {/* CTA */}
                {isCurrent ? (
                  <button className="btn btn-secondary" disabled style={{ width: '100%', justifyContent: 'center', fontSize: '0.8125rem' }}>
                    Current plan
                  </button>
                ) : isEnterprise ? (
                  <button
                    className="btn btn-secondary"
                    onClick={() => handleSubscribe('enterprise')}
                    style={{ width: '100%', justifyContent: 'center', fontSize: '0.8125rem' }}
                    data-testid="enterprise-contact-button"
                  >
                    <ExternalLink size={14} /> Contact sales
                  </button>
                ) : (
                  <button
                    className={isHighlight ? 'btn btn-primary' : 'btn btn-secondary'}
                    onClick={() => handleSubscribe(planId)}
                    disabled={subscribing !== null}
                    style={{ width: '100%', justifyContent: 'center', fontSize: '0.8125rem' }}
                    data-testid={`subscribe-${planId}-button`}
                  >
                    {subscribing === planId
                      ? <><div className="spinner" style={{ width: '14px', height: '14px' }} /> Processing...</>
                      : `Subscribe to ${plan.label}`
                    }
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      <p style={{ textAlign: 'center', fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))', marginTop: '2rem', maxWidth: '500px', margin: '2rem auto 0' }}>
        All plans billed monthly. Cancel anytime. Enterprise plans include SLA, SSO, and dedicated infrastructure.
      </p>
    </div>
  );
}
